import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { LoadingOverlay } from "@/components/ui/loading-spinner";
import { useConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Edit2,
  Download,
  FileText,
  ExternalLink,
  Loader2,
  Trash2,
  Copy,
  Eye,
  Globe,
  Lock,
  Tag,
  Calendar,
  User,
  Building,
  Film,
  Music,
  BookMarked,
  FileIcon,
  XCircle,
  Clock,
  HardDrive,
  Link,
} from "lucide-react";
import { catalogApi } from "@/lib/api/catalog";
import { cn } from "@/lib/utils";
import { useTenant } from "@/contexts/TenantContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { PaidAccessResourceAdminCard } from "@/components/catalog/PaidAccessResourceAdminCard";

// Resource type icons and colors
const getResourceTypeIcon = (type: string) => {
  switch (type?.toUpperCase()) {
    case "PDF":
      return <FileText className="w-5 h-5" />;
    case "VIDEO":
      return <Film className="w-5 h-5" />;
    case "AUDIO":
      return <Music className="w-5 h-5" />;
    case "EBOOK":
      return <BookMarked className="w-5 h-5" />;
    default:
      return <FileIcon className="w-5 h-5" />;
  }
};

const getResourceTypeColor = (type: string) => {
  switch (type?.toUpperCase()) {
    case "PDF":
      return "bg-red-100 text-red-700 border-red-200";
    case "VIDEO":
      return "bg-purple-100 text-purple-700 border-purple-200";
    case "AUDIO":
      return "bg-orange-100 text-orange-700 border-orange-200";
    case "EBOOK":
      return "bg-green-100 text-green-700 border-green-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

export function DigitalResourceDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { tenant } = useTenant();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [resourceData, setResourceData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { confirm, ConfirmDialog: ConfirmDialogComponent } = useConfirmDialog();
  const { hasPermission } = useAccessControl();
  const canEdit = hasPermission("catalog.edit") || hasPermission("digital.edit");
  const canDelete = hasPermission("catalog.delete") || hasPermission("digital.delete");
  const canManagePaidAccess =
    hasPermission("digital.paidAccess.manage") || hasPermission("digital.*");

  useEffect(() => {
    if (id) {
      loadResourceDetails();
    }
  }, [id]);

  const loadResourceDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await catalogApi.getDigitalResource(id!);
      setResourceData(data);
    } catch (err: any) {
      console.error("Failed to load resource details:", err);
      setError(err.response?.data?.message || "Failed to load resource details");
      toast.error("Failed to load resource details");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!resourceData?.fileUrl) return;

    try {
      setDownloading(true);
      const link = document.createElement("a");
      link.href = resourceData.fileUrl;
      link.download = `${resourceData.title}.${resourceData.type.toLowerCase()}`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Download started!");
    } catch (err) {
      console.error("Failed to download:", err);
      toast.error("Failed to download resource");
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = () => {
    confirm({
      title: "Delete Digital Resource",
      description: (
        <div className="space-y-2">
          <p>
            Are you sure you want to delete <strong>"{resourceData?.title}"</strong>?
          </p>
          <p className="text-sm text-gray-500">
            This will permanently delete the resource and its file. This action cannot be undone.
          </p>
        </div>
      ),
      variant: "danger",
      confirmLabel: "Delete Resource",
      onConfirm: async () => {
        try {
          setDeleting(true);
          await catalogApi.deleteDigitalResource(id!);
          toast.success("Digital resource deleted successfully!");
          navigate("/catalog");
        } catch (err: any) {
          toast.error(err.response?.data?.message || "Failed to delete resource");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "Unknown";
    const mb = bytes / (1024 * 1024);
    if (mb < 1) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return null;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0)
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingOverlay size="lg" label="Loading resource details..." />
      </div>
    );
  }

  if (error || !resourceData) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Digital Resource Details"
          description="View resource information"
          breadcrumbItems={[{ label: "Catalog", href: "/catalog" }, { label: "Digital Resource" }]}
          showSearch={false}
        />
        <Card className="border border-gray-200">
          <CardContent className="py-16 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Resource Not Found</h3>
            <p className="mb-6 text-sm text-gray-500">
              {error || "The requested resource could not be found."}
            </p>
            <Button onClick={() => navigate("/catalog")}>Back to Catalog</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allowDownload = (resourceData.metadata as any)?.allowDownload || false;
  const isExternalUrl =
    resourceData.isExternalUrl === true ||
    (resourceData.metadata as any)?.isExternalUrl === true ||
    resourceData.type === "WEBSITE";
  const alternateWebsiteUrl = (resourceData.metadata as any)?.websiteUrl as string | undefined;
  const showPaidAccessAdmin =
    canManagePaidAccess &&
    resourceData.type?.toUpperCase() === "PDF" &&
    !isExternalUrl &&
    Boolean(id);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Digital Resource Details"
        description="View and manage resource information"
        breadcrumbItems={[{ label: "Catalog", href: "/catalog" }, { label: resourceData.title }]}
        showSearch={false}
      />

      {/* Action Buttons */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {isExternalUrl && resourceData.fileUrl && (
          <Button variant="outline" asChild className="gap-2">
            <a href={resourceData.fileUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4" />
              Open Website
            </a>
          </Button>
        )}
        {!isExternalUrl && alternateWebsiteUrl && (
          <Button variant="outline" asChild className="gap-2">
            <a href={alternateWebsiteUrl} target="_blank" rel="noopener noreferrer">
              <Globe className="w-4 h-4" />
              Open Website
            </a>
          </Button>
        )}
        {resourceData.isPublic && (
          <Button
            variant="outline"
            onClick={() => {
              if (tenant?.slug) {
                sessionStorage.setItem("tenant_slug", tenant.slug);
              }
              navigate(`/view/${resourceData.id}`);
            }}
            className="gap-2"
          >
            <Eye className="w-4 h-4" />
            View/Read
          </Button>
        )}
        {allowDownload && !isExternalUrl && resourceData.fileUrl && (
          <Button
            variant="outline"
            onClick={handleDownload}
            disabled={downloading}
            className="gap-2"
          >
            {downloading ? (
              <>
                <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download
              </>
            )}
          </Button>
        )}
        {canEdit && (
          <Button
            variant="outline"
            onClick={() => navigate(`/catalog/digital/${resourceData.id}/edit`)}
            className="gap-2"
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </Button>
        )}
        {canDelete && (
          <Button
            variant="outline"
            onClick={handleDelete}
            disabled={deleting}
            className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
          >
            {deleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            Delete
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Thumbnail and Quick Info */}
        <div className="space-y-6">
          {/* Thumbnail Card */}
          <Card className="border border-gray-200 overflow-hidden">
            <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center aspect-[3/4]">
              {resourceData.thumbnailUrl ? (
                <img
                  src={resourceData.thumbnailUrl}
                  alt={resourceData.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                <div
                  className={cn(
                    "flex flex-col items-center gap-4 p-8 rounded-xl",
                    getResourceTypeColor(resourceData.type)
                  )}
                >
                  {getResourceTypeIcon(resourceData.type)}
                  <span className="text-sm font-medium">{resourceData.type}</span>
                </div>
              )}
            </div>
          </Card>

          {/* File Info */}
          <Card className="border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-900">File Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 flex items-center gap-2">
                  <HardDrive className="w-4 h-4" />
                  Size
                </span>
                <span className="font-medium">{formatFileSize(resourceData.fileSize)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 flex items-center gap-2">
                  <FileIcon className="w-4 h-4" />
                  Type
                </span>
                <Badge className={cn("border", getResourceTypeColor(resourceData.type))}>
                  {resourceData.type}
                </Badge>
              </div>
              {resourceData.mimeType && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">MIME Type</span>
                  <span className="font-mono text-xs text-gray-500">{resourceData.mimeType}</span>
                </div>
              )}
              {resourceData.duration && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Duration
                  </span>
                  <span className="font-medium">{formatDuration(resourceData.duration)}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Downloads
                </span>
                <span className="font-medium">{resourceData.downloadCount || 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Access Settings */}
          <Card className="border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-900">
                Access Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 flex items-center gap-2">
                  {resourceData.isPublic ? (
                    <Globe className="w-4 h-4 text-green-600" />
                  ) : (
                    <Lock className="w-4 h-4 text-gray-500" />
                  )}
                  Visibility
                </span>
                <Badge
                  className={cn(
                    "border",
                    resourceData.isPublic
                      ? "bg-green-100 text-green-700 border-green-200"
                      : "bg-gray-100 text-gray-700 border-gray-200"
                  )}
                >
                  {resourceData.isPublic ? "Public" : "Private"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Downloads
                </span>
                <Badge
                  className={cn(
                    "border",
                    allowDownload
                      ? "bg-primary/10 text-primary border-primary/20"
                      : "bg-gray-100 text-gray-700 border-gray-200"
                  )}
                >
                  {allowDownload ? "Allowed" : "Disabled"}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Classification */}
          <Card className="border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-900">
                Classification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* LC Classification hidden for now: the cataloging input sheet only captures
                  call number. The value is still stored and used for call number suggestions. */}
              {/* <div>
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  LC Classification
                </Label>
                <p className="font-mono text-sm mt-1">{resourceData.lcClassification || "—"}</p>
              </div> */}
              <div>
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Call Number
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <p className="font-mono text-sm">{resourceData.callNumber || "—"}</p>
                  {resourceData.callNumber && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => copyToClipboard(resourceData.callNumber, "Call number")}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Collection
                </Label>
                <p className="text-sm mt-1">{resourceData.collection?.name || "—"}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Resource Information */}
          <Card className="border border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <FileText className="w-5 h-5 text-primary" />
                Resource Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </Label>
                <p className="text-xl font-semibold text-gray-900 mt-1">{resourceData.title}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                    <User className="w-3 h-3" />
                    Authors
                  </Label>
                  <p className="mt-1">
                    {resourceData.authors && resourceData.authors.length > 0
                      ? resourceData.authors.join(", ")
                      : "—"}
                  </p>
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                    <Building className="w-3 h-3" />
                    Publisher
                  </Label>
                  <p className="mt-1">{resourceData.publisher || "—"}</p>
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Published Date
                </Label>
                <p className="mt-1">{resourceData.publishedDate || "—"}</p>
              </div>

              <div>
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  Subjects
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {resourceData.subjects && resourceData.subjects.length > 0 ? (
                    resourceData.subjects.map((subject: string, idx: number) => (
                      <Badge key={idx} variant="outline" className="bg-gray-50 text-gray-700">
                        {subject}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">No subjects assigned</span>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tags
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {resourceData.tags && resourceData.tags.length > 0 ? (
                    resourceData.tags.map((tag: string, idx: number) => (
                      <Badge key={idx} className="bg-primary/5 text-primary border-primary/20">
                        {tag}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">No tags assigned</span>
                  )}
                </div>
              </div>

              {resourceData.description && (
                <div>
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </Label>
                  <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                    {resourceData.description}
                  </p>
                </div>
              )}

              <div className="pt-4 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </Label>
                    <p className="mt-1 text-gray-600">
                      {new Date(resourceData.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </Label>
                    <p className="mt-1 text-gray-600">
                      {new Date(resourceData.updatedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Linked Biblio Record */}
          {resourceData.biblio && (
            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Link className="w-5 h-5 text-primary" />
                  Linked Physical Record
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div>
                    <p className="font-semibold text-gray-900">{resourceData.biblio.title}</p>
                    <p className="text-sm text-gray-500">
                      {resourceData.biblio.authors?.join(", ") || "Unknown author"}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/catalog/physical/${resourceData.biblio.id}`)}
                    className="gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Record
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {showPaidAccessAdmin && (
        <PaidAccessResourceAdminCard digitalResourceId={id!} resourceTitle={resourceData.title} />
      )}

      <ConfirmDialogComponent />
    </div>
  );
}
